import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api-client';
import type {
  EmployeeSummaryDto,
  IdCount,
  LocationCount,
  OnboardingSummaryDto,
} from '../../lib/types';
import { useEmploymentStatuses } from '../../lib/useOrgLookups';
import { CardQueryState } from '@payforge/ui';
import { WidgetCard } from '../../components/WidgetCard';
import { WidgetEmptyState } from '../../components/WidgetEmptyState';
import { SummaryRow } from '../../components/SummaryRow';
import { SegmentedToggle } from '../../components/SegmentedToggle';
import { ChevronDownIcon, ChevronRightIcon } from '../../components/icons';
import { initials } from '../directory/avatar-utils';
import locationEmptyArt from '../../assets/illustrations/location.png';
import employmentStatusEmptyArt from '../../assets/illustrations/employeestatus.png';
import onboardingEmptyArt from '../../assets/illustrations/onboarding at payforge.png';

// Data-viz palette — intentionally separate from brand tokens. Cohesive
// blue -> purple -> pink scheme from the headcount-chart design.
const VIZ_PALETTE = [
  '#1eabf1',
  '#168acf',
  '#81d1fa',
  '#5e3baf',
  '#7e5bc0',
  '#452d9e',
  '#d62162',
  '#ab1a58',
];

type DonutSlice = { key: string | number; label: string; count: number };

type SliceSpec = {
  key: string | number;
  label: string;
  count: number;
  color: string;
  pct: number;
};

// Shared colour + percentage assignment so the donut, bars and list all colour a
// given slice identically (palette index by position).
function computeSpecs(slices: DonutSlice[]): SliceSpec[] {
  const total = slices.reduce((s, d) => s + d.count, 0);
  return slices.map((slice, i) => ({
    ...slice,
    color: VIZ_PALETTE[i % VIZ_PALETTE.length] ?? '#94a3b8',
    pct: total > 0 ? Math.round((slice.count / total) * 100) : 0,
  }));
}

// Ranked legend list: colour dot + label + %/count. Used beside the donut (chart
// view) and standalone as the Location "List" view.
function HeadcountLegend({
  specs,
  hrefFor,
}: {
  specs: SliceSpec[];
  hrefFor: (key: string | number) => string;
}) {
  return (
    <ul className="w-full @md:flex-1 @md:min-w-0">
      {specs.map(({ key, label, count, color, pct }) => (
        <li key={key}>
          <Link
            to={hrefFor(key)}
            aria-label={label}
            title={label}
            className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 text-sm hover:bg-slate-50"
          >
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
            <span className="min-w-0 flex-1 truncate text-slate-700">{label}</span>
            <span className="flex w-[88px] shrink-0 items-center justify-between">
              <span className="text-slate-400">{pct}%</span>
              <span className="font-semibold text-slate-900">{count.toLocaleString()}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function HeadcountDonut({
  specs,
  hrefFor,
}: {
  specs: SliceSpec[];
  hrefFor: (key: string | number) => string;
}) {
  const navigate = useNavigate();
  const r = 70;
  const strokeWidth = 26;
  const C = 2 * Math.PI * r;
  const total = specs.reduce((s, d) => s + d.count, 0);

  type Arc = SliceSpec & { dasharray: string; dashoffset: number };
  const arcs = specs.reduce<{ list: Arc[]; cum: number }>(
    (acc, s) => {
      const fraction = total > 0 ? s.count / total : 0;
      return {
        list: [...acc.list, { ...s, dasharray: `${fraction * C} ${C}`, dashoffset: -acc.cum * C }],
        cum: acc.cum + fraction,
      };
    },
    { list: [], cum: 0 },
  ).list;

  return (
    <div className="flex w-full flex-col items-center gap-6 @md:flex-row @md:items-center @md:gap-8">
      <div className="relative shrink-0">
        <svg width="208" height="208" viewBox="-104 -104 208 208" aria-label={`Donut chart: ${total} total people`}>
          <g transform="rotate(-90)">
            {arcs.map(({ key, dasharray, dashoffset, color, label, count, pct }) => {
              const href = hrefFor(key);
              const tooltip = `${label} — ${count} (${pct}%)`;
              return (
                <circle
                  key={key}
                  cx={0}
                  cy={0}
                  r={r}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={dasharray}
                  strokeDashoffset={dashoffset}
                  className="cursor-pointer transition-opacity hover:opacity-80"
                  role="button"
                  aria-label={tooltip}
                  tabIndex={0}
                  onClick={() => void navigate(href)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void navigate(href);
                    }
                  }}
                >
                  <title>{tooltip}</title>
                </circle>
              );
            })}
          </g>
          <text x={0} y={-10} textAnchor="middle" fontSize={14} fill="#94979e" fontFamily="inherit">
            Total people
          </text>
          <text x={0} y={22} textAnchor="middle" fontSize={32} fontWeight={700} fill="#272a31" fontFamily="inherit">
            {total}
          </text>
        </svg>
      </div>
      <HeadcountLegend specs={specs} hrefFor={hrefFor} />
    </div>
  );
}

// Horizontal bar view: one labelled track per slice, width = its percentage.
function HeadcountBars({
  specs,
  hrefFor,
}: {
  specs: SliceSpec[];
  hrefFor: (key: string | number) => string;
}) {
  return (
    <ul className="flex w-full flex-col gap-3">
      {specs.map(({ key, label, count, color, pct }) => (
        <li key={key}>
          <Link
            to={hrefFor(key)}
            aria-label={label}
            title={label}
            className="-mx-2 block rounded-lg px-2 py-1.5 hover:bg-slate-50"
          >
            <div className="mb-1.5 flex items-center justify-between text-sm">
              <span className="min-w-0 truncate text-slate-700">{label}</span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-slate-400">{pct}%</span>
                <span className="font-semibold text-slate-900">{count.toLocaleString()}</span>
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

type HeadcountView = 'chart' | 'bar' | 'list';

// One headcount visualization with switchable views (donut+legend / bars / list),
// sharing a single slice-spec computation so colours stay consistent across views.
function HeadcountViz({
  slices,
  hrefFor,
  view,
}: {
  slices: DonutSlice[];
  hrefFor: (key: string | number) => string;
  view: HeadcountView;
}) {
  const specs = computeSpecs(slices);
  if (view === 'bar') return <HeadcountBars specs={specs} hrefFor={hrefFor} />;
  if (view === 'list') return <HeadcountLegend specs={specs} hrefFor={hrefFor} />;
  return <HeadcountDonut specs={specs} hrefFor={hrefFor} />;
}

function ReportsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M22 20c0-3.314-1.79-6-4-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function reportFirstName(r: EmployeeSummaryDto) {
  return (r.preferredName && r.preferredName.trim()) || r.firstName;
}

function reportName(r: EmployeeSummaryDto) {
  return `${reportFirstName(r)} ${r.lastName}`.trim();
}

// One report row: initials avatar + Name + work email, a trailing chevron, and a
// bottom divider. The whole row links to the report's profile.
function ReportRow({ report }: { report: EmployeeSummaryDto }) {
  const name = reportName(report);
  return (
    <li>
      <Link
        to={`/people/${report.id}`}
        className="flex h-[52px] items-center justify-between gap-3 border-b border-line last:border-b-0 hover:bg-slate-50"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-500">
            {initials(name)}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-slate-900">{name}</span>
            <span className="block truncate text-xs text-slate-500">{report.workEmail}</span>
          </span>
        </span>
        <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" />
      </Link>
    </li>
  );
}

// W2 — My Direct Reports. A list of the viewer's reports (avatar + name + email),
// sorted alphabetically by first name; each row links to the report's profile. The
// widget hides entirely when the viewer has no reports — that single rule covers all
// role-gating (employee-no-reports / admin-no-reports both hide).
export function MyDirectReportsWidget() {
  const reports = useQuery({
    queryKey: ['home', 'my-direct-reports'],
    queryFn: () => api.get<EmployeeSummaryDto[]>('/v1/home/my-direct-reports'),
  });

  const list = [...(Array.isArray(reports.data) ? reports.data : [])].sort((a, b) =>
    reportFirstName(a).localeCompare(reportFirstName(b)),
  );

  // No reports → the widget renders nothing at all (spec: hides entirely).
  // Wait until reports.isLoading resolves before deciding.
  if (!reports.isLoading && !reports.isError && list.length === 0) {
    return null;
  }

  return (
    <WidgetCard icon={<ReportsIcon />} title="My Direct Reports">
      <CardQueryState
        query={reports}
        label="My Direct Reports"
        skeleton={
          <ul className="space-y-3">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-slate-200" />
                <span className="flex-1 space-y-1.5">
                  <span className="block h-3 w-32 animate-pulse rounded bg-slate-200" />
                  <span className="block h-2.5 w-24 animate-pulse rounded bg-slate-100" />
                </span>
              </li>
            ))}
          </ul>
        }
      >
        {/* Capped height, tightened ~20% below the other widgets (kept in sync with
            Company Holidays); extra reports scroll instead of growing the card. */}
        <ul className="max-h-[17.6rem] overflow-y-auto pr-1">
          {list.map((r) => (
            <ReportRow key={r.id} report={r} />
          ))}
        </ul>
      </CardQueryState>
    </WidgetCard>
  );
}

function LocationIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export function LocationPlaceholder() {
  const [view, setView] = useState<'chart' | 'list'>('chart');
  const query = useQuery({
    queryKey: ['home', 'by-location'],
    queryFn: () => api.get<LocationCount[]>('/v1/home/by-location'),
  });

  const data = Array.isArray(query.data) ? query.data : [];

  const slices: DonutSlice[] = data.map((loc) => ({
    key: loc.locationId,
    label: loc.name,
    count: loc.count,
  }));

  // The People Directory tab filters by location name, so deep-link by name (not id).
  const nameById = new Map<number, string>(data.map((loc) => [loc.locationId, loc.name]));

  return (
    <WidgetCard
      icon={<LocationIcon />}
      title="Location"
      className="@container"
      headerAction={
        <SegmentedToggle
          ariaLabel="Location view"
          value={view}
          onChange={setView}
          options={[
            { value: 'chart', label: 'Chart' },
            { value: 'list', label: 'List' },
          ]}
        />
      }
    >
      <CardQueryState
        query={query}
        label="Location"
        skeleton={
          <div className="flex flex-1 items-center justify-center py-8">
            <span className="h-[140px] w-[140px] animate-pulse rounded-full bg-slate-200" />
          </div>
        }
        empty={{
          when: data.length === 0,
          node: (
            <WidgetEmptyState
              image={locationEmptyArt}
              title="No location data yet"
              description="Headcount by location appears here once employees are assigned."
            />
          ),
        }}
      >
        <div className={`flex flex-1 ${view === 'chart' ? 'items-center justify-center' : ''}`}>
          <HeadcountViz
            slices={slices}
            view={view}
            hrefFor={(id) =>
              `/people/directory?location=${encodeURIComponent(nameById.get(Number(id)) ?? '')}`
            }
          />
        </div>
      </CardQueryState>
    </WidgetCard>
  );
}

function EmploymentStatusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function EmploymentStatusPlaceholder() {
  const [view, setView] = useState<'chart' | 'bar'>('chart');
  const query = useQuery({
    queryKey: ['home', 'by-employment-status'],
    queryFn: () => api.get<IdCount[]>('/v1/home/by-employment-status'),
  });
  const statusesQuery = useEmploymentStatuses();

  const statusNameById = new Map<number, string>(
    (statusesQuery.data ?? []).map((s) => [s.id, s.name]),
  );

  const data = Array.isArray(query.data) ? query.data : [];

  const slices: DonutSlice[] = data.map((item) => ({
    key: item.id,
    label: statusNameById.get(item.id) ?? `Status #${item.id}`,
    count: item.count,
  }));

  return (
    <WidgetCard
      icon={<EmploymentStatusIcon />}
      title="Employment Status"
      className="@container"
      headerAction={
        <SegmentedToggle
          ariaLabel="Employment status view"
          value={view}
          onChange={setView}
          options={[
            { value: 'chart', label: 'Chart' },
            { value: 'bar', label: 'Bar' },
          ]}
        />
      }
    >
      <CardQueryState
        query={query}
        label="Employment Status"
        skeleton={
          <div className="flex flex-1 items-center justify-center py-8">
            <span className="h-[140px] w-[140px] animate-pulse rounded-full bg-slate-200" />
          </div>
        }
        empty={{
          when: data.length === 0,
          node: (
            <WidgetEmptyState
              image={employmentStatusEmptyArt}
              title="No employment-status data yet"
              description="Headcount by employment status appears here once it's set."
            />
          ),
        }}
      >
        <div className={`flex flex-1 ${view === 'chart' ? 'items-center justify-center' : ''}`}>
          <HeadcountViz slices={slices} view={view} hrefFor={() => `/people/list`} />
        </div>
      </CardQueryState>
    </WidgetCard>
  );
}

function pluralPeople(count: number) {
  return count === 1 ? 'person' : 'people';
}

function verb(count: number) {
  return count === 1 ? 'has' : 'have';
}

export function OnboardingAtPayforgePlaceholder({ isAdmin }: { isAdmin: boolean }) {
  const summary = useQuery({
    queryKey: ['home', 'onboarding-summary'],
    queryFn: () => api.get<OnboardingSummaryDto>('/v1/home/onboarding-summary'),
  });

  const reports = useQuery({
    queryKey: ['home', 'my-direct-reports'],
    queryFn: () => api.get<EmployeeSummaryDto[]>('/v1/home/my-direct-reports'),
  });

  // Role gate: employee with no reports → hidden entirely.
  // Wait until reports query settles before deciding to avoid a flash.
  if (!isAdmin && !reports.isLoading && (Array.isArray(reports.data) ? reports.data : []).length === 0) {
    return null;
  }

  const onboardingIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M16 11l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <WidgetCard icon={onboardingIcon} title="Onboarding at Payforge">
      <CardQueryState
        query={summary}
        label="Onboarding"
        skeleton={
          <div className="space-y-3">
            <span className="block h-4 w-48 animate-pulse rounded bg-slate-200" />
            <span className="block h-4 w-40 animate-pulse rounded bg-slate-200" />
          </div>
        }
      >
        <OnboardingBody data={summary.data as OnboardingSummaryDto} />
      </CardQueryState>
    </WidgetCard>
  );
}

// Disclosure chevron that rotates when its parent <details> is open.
function DisclosureChevron() {
  return (
    <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
  );
}

const overdueIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const unfinishedIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function OnboardingBody({ data }: { data: OnboardingSummaryDto }) {
  const overdueCount = Number(data.overdueCount);
  const unfinishedCount = Number(data.unfinishedCount);
  const overduePeople = Array.isArray(data.overduePeople) ? data.overduePeople : [];
  const unfinishedPeople = Array.isArray(data.unfinishedPeople) ? data.unfinishedPeople : [];

  const allOnSchedule = overdueCount === 0 && unfinishedCount === 0;

  if (allOnSchedule) {
    return (
      <WidgetEmptyState
        image={onboardingEmptyArt}
        title="All onboardings on schedule. ✨"
      />
    );
  }

  return (
    <ul className="flex max-h-[22rem] flex-col gap-3 overflow-y-auto pr-1">
      {overdueCount > 0 && (
        <li>
          <OnboardingGroup
            icon={overdueIcon}
            iconClassName="bg-red-50 text-red-600"
            title={`${overdueCount} ${pluralPeople(overdueCount)} ${verb(overdueCount)} overdue onboarding tasks`}
            people={overduePeople.map((p) => ({
              employeeId: p.employeeId,
              displayName: p.displayName,
              meta: `${p.overdueTaskCount} overdue · ${p.daysOverdue}d`,
              metaClassName: 'text-red-500',
            }))}
          />
        </li>
      )}

      {unfinishedCount > 0 && (
        <li>
          <OnboardingGroup
            icon={unfinishedIcon}
            iconClassName="bg-amber-50 text-amber-600"
            title={`${unfinishedCount} ${pluralPeople(unfinishedCount)} ${verb(unfinishedCount)} unfinished onboarding tasks`}
            people={unfinishedPeople.map((p) => ({
              employeeId: p.employeeId,
              displayName: p.displayName,
              meta: `${p.unfinishedTaskCount} unfinished`,
              metaClassName: 'text-amber-600',
            }))}
          />
        </li>
      )}
    </ul>
  );
}

type OnboardingGroupPerson = {
  employeeId: number;
  displayName: string;
  meta: string;
  metaClassName: string;
};

// One collapsible onboarding group. Uses a native <details>/<summary> so the
// summary row toggles its person list without extra state, and reuses SummaryRow
// for the design-system look (summary accepts flow content, so this stays valid HTML).
function OnboardingGroup({
  icon,
  iconClassName,
  title,
  people,
}: {
  icon: ReactNode;
  iconClassName: string;
  title: string;
  people: OnboardingGroupPerson[];
}) {
  return (
    <details className="group">
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <SummaryRow
          icon={icon}
          iconClassName={iconClassName}
          title={title}
          trailing={<DisclosureChevron />}
        />
      </summary>
      <ul className="mt-1 space-y-0.5 pl-2">
        {people.map((p) => (
          <li key={p.employeeId}>
            <Link
              to={`/people/${p.employeeId}?tab=onboarding`}
              className="-mx-1 flex items-center justify-between rounded px-1 py-1 text-sm hover:bg-slate-50"
            >
              <span className="font-medium text-slate-800">{p.displayName}</span>
              <span className={`shrink-0 text-xs ${p.metaClassName}`}>{p.meta}</span>
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}
